import React, { useEffect, useCallback } from "react"
import ZeefChatHeader from "./containers/ZeefChatHeader"
import ZeefMessagesArea from "./containers/ZeefMessagesArea"
import ZeefMessageInput from "./containers/ZeefMessageInput"
import ZeefSidePanel from "./containers/ZeefSidePanel"
import { useZeefUser } from "../hooks/useZeefUser"
import { useZeefMessages } from "../hooks/useZeefMessages"
import { useZeefSocial } from "../hooks/useZeefSocial"
import { useZeefMentions } from "../hooks/useZeefMentions"
import { useZeefKeyboardShortcuts } from "../hooks/useZeefKeyboardShortcuts"
import { useZeefWebSocket } from "../hooks/useZeefWebSocket"
import { Message, Follow, User } from "../ZeefTypes"
import ZeefService from "../ZeefService"
import "../ZeefChat.scss"

interface ZeefChatProps {
  isVisible?: boolean
  currentProject?: any
}

const ZeefChat: React.FC<ZeefChatProps> = ({ isVisible = true, currentProject }) => {
  // Initialize all hooks
  const userHook = useZeefUser()
  const messagesHook = useZeefMessages()
  const socialHook = useZeefSocial()
  const mentionsHook = useZeefMentions()
  const keyboardHook = useZeefKeyboardShortcuts()
  const webSocketHook = useZeefWebSocket()

  const browserLoadTime = React.useRef(new Date().toISOString())

  // Enhanced input change handler that includes mention detection
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const inputMessage = event.target.value
    const cursorPosition = event.target.selectionStart || 0

    messagesHook.handleInputChange(event)
    mentionsHook.checkForMention(inputMessage, cursorPosition, socialHook.getAllSocialUsers())
  }, [messagesHook, mentionsHook, socialHook])

  // Enhanced input key down handler that includes mention handling
  const handleInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    const mentionHandled = mentionsHook.handleMentionKeyDown(event)
    
    if (!mentionHandled) {
      // Handle escape key for regular input
      if (event.key === "Escape") {
        event.preventDefault()
        event.stopPropagation()
        if (mentionsHook.showMentionDropdown) {
          mentionsHook.hideMentionDropdown()
        } else {
          event.currentTarget.blur()
        }
        return
      }

      // Handle mention selection
      if (mentionsHook.showMentionDropdown && mentionsHook.mentionSuggestions.length > 0) {
        if (event.key === "Enter" || event.key === "Tab") {
          mentionsHook.handleMentionSelect(
            mentionsHook.mentionSuggestions[0],
            messagesHook.inputMessage,
            (newMessage) => {
              // This is a bit of a workaround - we need to update the input message
              const syntheticEvent = {
                target: { value: newMessage }
              } as React.ChangeEvent<HTMLInputElement>
              messagesHook.handleInputChange(syntheticEvent)
            },
            messagesHook.messageInputRef
          )
          event.preventDefault()
        }
      }
    }
  }, [mentionsHook, messagesHook])

  // Enhanced send message handler
  const handleSendMessage = useCallback((event: React.FormEvent) => {
    messagesHook.handleSendMessage(event, userHook.currentUser, socialHook.getAllSocialUsers())
  }, [messagesHook, userHook.currentUser, socialHook])

  // Enhanced mention select handler
  const handleMentionSelect = useCallback((user: User) => {
    mentionsHook.handleMentionSelect(
      user,
      messagesHook.inputMessage,
      (newMessage) => {
        const syntheticEvent = {
          target: { value: newMessage }
        } as React.ChangeEvent<HTMLInputElement>
        messagesHook.handleInputChange(syntheticEvent)
      },
      messagesHook.messageInputRef
    )
  }, [mentionsHook, messagesHook])

  // WebSocket event handlers
  const setupWebSocketEvents = useCallback(() => {
    webSocketHook.setEventHandlers({
      onMessageReceived: (message: Message) => {
        console.log("WebSocket message received:", message)

        // Check if this is our own message coming back
        const isOwnMessage = message.sender?.id === userHook.currentUser?.id

        if (isOwnMessage) {
          // Update existing user message with server data
          messagesHook.updateMessage(
            // Find the message by content and lack of serverId
            messagesHook.messages.find(msg => 
              msg.type === "user" && 
              msg.content === ZeefService.getMessageDisplayText(message) && 
              !(msg as any).serverId
            )?.id || "",
            {
              id: message.id,
              serverId: message.id,
              zeefMessage: message,
              timestamp: message.created_at,
              thread_root_id: message.thread_root_id,
            }
          )
          return
        }

        // Prevent duplicates for received messages
        const messageExists = messagesHook.messages.some(
          msg => msg.id === message.id || (msg as any).serverId === message.id
        )
        if (messageExists) {
          return
        }

        // Add new received message
        const receivedMessage = {
          id: message.id,
          type: "received" as const,
          content: ZeefService.getMessageDisplayText(message),
          timestamp: message.created_at,
          sender: message.sender,
          zeefMessage: message,
          isHistorical: false,
          isBroadcast: message.type === "broadcast",
          thread_root_id: message.thread_root_id,
          serverId: message.id,
        }

        messagesHook.addMessage(receivedMessage)
      },

      onFollowAdded: (follow: Follow) => {
        console.log("Follow added:", follow)
        
        if (follow.following_id === userHook.currentUser?.id && follow.follower) {
          socialHook.addFollower(follow.follower)
        }
        
        if (follow.follower_id === userHook.currentUser?.id && follow.following) {
          socialHook.addFollowing(follow.following)
        }
        
        // Refresh for accurate counts
        socialHook.loadSocialData()
      },

      onFollowRemoved: (follow: Follow) => {
        console.log("Follow removed:", follow)
        
        if (follow.following_id === userHook.currentUser?.id) {
          socialHook.removeFollower(follow.follower_id)
        }
        
        if (follow.follower_id === userHook.currentUser?.id) {
          socialHook.removeFollowing(follow.following_id)
        }
        
        // Refresh for accurate counts
        socialHook.loadSocialData()
      },

      onUserOnline: (data: { user_id: string; timestamp: string }) => {
        console.log(`User ${data.user_id} came online at ${data.timestamp}`)
        socialHook.addUserOnline(data.user_id)
      },

      onUserOffline: (data: { user_id: string; timestamp: string }) => {
        console.log(`User ${data.user_id} went offline at ${data.timestamp}`)
        socialHook.removeUserOffline(data.user_id)
      },

      onError: (error: string) => {
        console.error("ZEEF error:", error)
        
        const errorMessage = {
          id: Date.now().toString(),
          type: "error" as const,
          content: error,
          timestamp: new Date().toISOString(),
          isHistorical: false,
        }
        
        messagesHook.addMessage(errorMessage)
      },
    })
  }, [webSocketHook, userHook.currentUser, messagesHook, socialHook])

  // Global keyboard event handler
  const handleGlobalKeyDown = useCallback((event: KeyboardEvent) => {
    // Handle message identifiers for keyboard shortcuts
    if (isVisible) {
      const isInputFocused = document.activeElement === messagesHook.messageInputRef.current
      const isOtherInputFocused = document.activeElement?.matches("input, textarea, select, [contenteditable]")

      if (event.key === "r" && !keyboardHook.showMessageIdentifiers && !isOtherInputFocused) {
        event.preventDefault()
        keyboardHook.generateMessageIdentifiers(messagesHook.messages)
        keyboardHook.showIdentifiers()
      } else if (keyboardHook.showMessageIdentifiers) {
        if (event.key === "Escape") {
          event.preventDefault()
          keyboardHook.hideIdentifiers()
        } else if (event.key === "Backspace" && !isOtherInputFocused) {
          event.preventDefault()
          keyboardHook.handleIdentifierBackspace()
        } else if (event.key.length === 1 && /[a-z]/i.test(event.key) && !isOtherInputFocused) {
          event.preventDefault()
          keyboardHook.handleIdentifierKeyPress(
            event.key.toLowerCase(),
            messagesHook.messages,
            messagesHook.handleReplyToMessage
          )
        }
      }
    }
  }, [isVisible, keyboardHook, messagesHook])

  // Setup component
  useEffect(() => {
    const initializeComponent = async () => {
      await userHook.loadCurrentUser()
      await webSocketHook.initializeWebSocket()
      setupWebSocketEvents()
      await socialHook.loadSocialData()
      await messagesHook.loadMessageHistory(browserLoadTime.current)
      await socialHook.loadOnlineUsers()
    }

    initializeComponent()

    // Add global event listeners
    document.addEventListener("keydown", handleGlobalKeyDown)
    document.addEventListener("click", keyboardHook.handleGlobalClick)

    return () => {
      webSocketHook.cleanup()
      document.removeEventListener("keydown", handleGlobalKeyDown)
      document.removeEventListener("click", keyboardHook.handleGlobalClick)
    }
  }, []) // Empty dependency array for mount/unmount only

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesHook.scrollToBottom()
  }, [messagesHook.messages.length])

  return (
    <div className="zeef-chat-widget with-side-panel" onClick={(e) => keyboardHook.handleWidgetClick(e.nativeEvent)}>
      <div className="chat-main-container">
        <div className="messages-container" ref={messagesHook.messagesContainerRef}>
          <ZeefChatHeader
            currentUser={userHook.currentUser}
            isLoadingUser={userHook.isLoadingUser}
            isConnected={webSocketHook.isConnected}
            isEditingUsername={userHook.isEditingUsername}
            editingUsername={userHook.editingUsername}
            onUsernameClick={userHook.handleUsernameClick}
            onUsernameChange={userHook.handleUsernameInputChange}
            onUsernameKeyPress={userHook.handleUsernameKeyPress}
            onSaveUsername={userHook.saveUsername}
            onCancelUsername={userHook.cancelUsernameEdit}
            isUpdatingUsername={userHook.isUpdatingUsername}
          />

          <ZeefMessagesArea
            messages={messagesHook.messages}
            isLoadingMessages={messagesHook.isLoadingMessages}
            currentUser={userHook.currentUser}
            showMessageIdentifiers={keyboardHook.showMessageIdentifiers}
            filteredIdentifierMap={keyboardHook.filteredIdentifierMap}
            onReplyClick={messagesHook.handleReplyToMessage}
            calculateThreadDepths={messagesHook.calculateThreadDepths}
          />
        </div>

        <ZeefMessageInput
          inputMessage={messagesHook.inputMessage}
          isConnected={webSocketHook.isConnected}
          isLoading={messagesHook.isLoading}
          isLoadingUser={userHook.isLoadingUser}
          isBroadcastMode={messagesHook.isBroadcastMode}
          replyingToMessage={messagesHook.replyingToMessage}
          showMentionDropdown={mentionsHook.showMentionDropdown}
          mentionSuggestions={mentionsHook.mentionSuggestions}
          messageInputRef={messagesHook.messageInputRef}
          onInputChange={handleInputChange}
          onInputKeyDown={handleInputKeyDown}
          onSendMessage={handleSendMessage}
          onCancelReply={messagesHook.handleCancelReply}
          onToggleBroadcastMode={messagesHook.toggleBroadcastMode}
          onMentionSelect={handleMentionSelect}
        />
      </div>

      <ZeefSidePanel
        followers={socialHook.followers}
        following={socialHook.following}
        mutualFollows={socialHook.mutualFollows}
        onlineUsers={socialHook.onlineUsers}
        loading={socialHook.loading}
        searchQuery={socialHook.searchQuery}
        searchResults={socialHook.searchResults}
        isSearching={socialHook.isSearching}
        isFollowingUser={socialHook.isFollowingUser}
        isUnfollowingUser={socialHook.isUnfollowingUser}
        unfollowingUserId={socialHook.unfollowingUserId}
        onSearchChange={socialHook.handleSearchInputChange}
        onFollowUser={socialHook.followUser}
        onUnfollowUser={socialHook.unfollowUser}
      />
    </div>
  )
}

export default ZeefChat
